/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing';
import { useCatalogCategoryManager } from '../hooks/useCatalogCategoryManager';
import type { CategoryRecord } from '../../domain/categoryTypes';
import type { CategoryFilterState } from '../../domain/catalogCategoryTypes';
import { buildCategoryTreeGroups, reorderSiblingGroup } from '../../domain/categoryMasterTree';
import { CategoryMasterTreeView } from './CategoryMasterTreeView';
import { MastersSectionHeader } from './MastersSectionHeader';
import { MastersListFiltersPanel } from '../../../shared/filters/MastersListFiltersPanel';
import { MasterDetailModal } from './MasterDetailModal';
import { MastersSectionListShell } from './MastersSectionListShell';
import {
  MASTERS_WIDE_LAYOUT_BREAKPOINT,
  mastersFilterOptions,
  type MasterSectionStats,
} from './mastersSectionShared';
import { CategoryRequestModal } from './CategoryRequestModal';
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useCategoryMasterList } from './useCategoryMasterList';
import {
  CatalogMasterConfirmModal,
  type CatalogMasterConfirmSpec,
} from './CatalogMasterConfirmModal';

export type CategoriesMasterSectionProps = {
  canEdit: boolean;
  onOpenCsv?: () => void;
  onStatsChange?: (stats: MasterSectionStats) => void;
  onBindReload?: (reload: () => void) => void;
};

const FILTER_OPTIONS = mastersFilterOptions({ active: 'Activas', inactive: 'Inactivas', all: 'Todas' });
const LIST_NOUNS = { singular: 'categoría', plural: 'categorías' };

export const CategoriesMasterSection = ({
  canEdit,
  onOpenCsv,
  onStatsChange,
  onBindReload,
}: CategoriesMasterSectionProps) => {
  const { width } = useWindowDimensions();
  const isWide = width >= MASTERS_WIDE_LAYOUT_BREAKPOINT;
  const [filterState, setFilterState] = useState<CategoryFilterState>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [hasPendingOrder, setHasPendingOrder] = useState(false);
  const pendingOrderRef = useRef<Map<string, number>>(new Map());

  const {
    visibleGroups,
    allCategories,
    loading,
    error,
    itemCount,
    reload,
    patchCategoryActive,
    revertCategoryActivePatch,
    patchSortOrders,
    clearSortOrderPatch,
  } = useCategoryMasterList(filterState, searchQuery);

  const manager = useCatalogCategoryManager({
    onCategoriesChanged: () => reload({ silent: true }),
    skipListLoad: true,
  });

  useEffect(() => {
    onBindReload?.(reload);
    return () => onBindReload?.(() => {});
  }, [reload, onBindReload]);

  const [detailCategory, setDetailCategory] = useState<CategoryRecord | null>(null);
  const [categoryRequestOpen, setCategoryRequestOpen] = useState(false);
  const [categoryConfirmSpec, setCategoryConfirmSpec] = useState<CatalogMasterConfirmSpec | null>(null);

  const stats = useMemo<MasterSectionStats>(() => {
    const active = allCategories.filter((category) => category.isActive).length;
    return {
      all: allCategories.length,
      active,
      inactive: allCategories.length - active,
    };
  }, [allCategories]);

  useEffect(() => {
    onStatsChange?.(stats);
  }, [onStatsChange, stats]);

  const childCountByParentId = useMemo(() => {
    const groups = buildCategoryTreeGroups(allCategories);
    return new Map(groups.map((group) => [group.parent.id, group.children.length]));
  }, [allCategories]);

  const onOpenDetail = useCallback((category: CategoryRecord) => {
    setDetailCategory(category);
  }, []);

  const onCloseDetail = useCallback(() => {
    setDetailCategory(null);
  }, []);

  const applyToggleActive = useCallback(
    (category: CategoryRecord, nextActive: boolean) => {
      const patch: Record<string, boolean> = { [category.id]: nextActive };
      const togglingIds = [category.id];

      if (!category.parentId) {
        for (const child of allCategories) {
          if (child.parentId === category.id) {
            patch[child.id] = nextActive;
            togglingIds.push(child.id);
          }
        }
      } else if (nextActive) {
        const parent = allCategories.find((row) => row.id === category.parentId);
        if (parent && !parent.isActive) {
          patch[parent.id] = true;
          togglingIds.push(parent.id);
        }
      }

      patchCategoryActive(patch);

      void manager
        .toggleCategoryActive(category, nextActive, {
          filterState,
          childCount: childCountByParentId.get(category.id) ?? 0,
          togglingIds,
          wokeParent: Boolean(category.parentId && patch[category.parentId!] === true),
        })
        .catch(() => revertCategoryActivePatch(togglingIds));
    },
    [
      allCategories,
      childCountByParentId,
      filterState,
      manager,
      patchCategoryActive,
      revertCategoryActivePatch,
    ],
  );

  const onToggleActive = useCallback(
    (category: CategoryRecord, nextActive: boolean) => {
      // Desactivar una categoría con subcategorías las oculta en cascada:
      // primero confirmación con impacto, recién después se patchea el estado.
      const children = !category.parentId
        ? allCategories.filter((row) => row.parentId === category.id)
        : [];
      if (!nextActive && children.length > 0) {
        const names = children.map((c) => c.name).join(', ');
        setCategoryConfirmSpec({
          kind: 'confirm',
          title: 'Desactivar categoría',
          entityName: category.name,
          message: `Se ocultan ${category.name} + ${children.length} subcategoría${
            children.length === 1 ? '' : 's'
          }: ${names}. Las podés volver a mostrar desde Inactivas.`,
          confirmLabel: 'Desactivar',
          destructive: true,
          iconName: 'eye-off-outline',
          onConfirm: () => {
            setCategoryConfirmSpec(null);
            applyToggleActive(category, false);
          },
        });
        return;
      }
      applyToggleActive(category, nextActive);
    },
    [allCategories, applyToggleActive],
  );

  // ── Modo ordenar ─────────────────────────────────────────────────────────
  // Los movimientos son locales (sortOverrides) y se guardan en un solo RPC.
  const enterReorderMode = useCallback(() => {
    // El orden vive por grupo de hermanos completo: filtro "Todas" y sin búsqueda
    // para que las flechas nunca swapeen contra una categoría oculta.
    setFilterState('all');
    setSearchQuery('');
    setReorderMode(true);
  }, []);

  const onMoveCategory = useCallback(
    (category: CategoryRecord, direction: -1 | 1) => {
      const items = reorderSiblingGroup(allCategories, category.id, direction);
      if (!items) return;
      patchSortOrders(items);
      for (const item of items) pendingOrderRef.current.set(item.id, item.sortOrder);
      setHasPendingOrder(true);
    },
    [allCategories, patchSortOrders],
  );

  const exitReorderMode = useCallback(() => {
    pendingOrderRef.current.clear();
    setHasPendingOrder(false);
    setReorderMode(false);
  }, []);

  const onCancelOrder = useCallback(() => {
    clearSortOrderPatch();
    exitReorderMode();
  }, [clearSortOrderPatch, exitReorderMode]);

  const onSaveOrder = useCallback(async () => {
    const items = [...pendingOrderRef.current.entries()].map(([id, sortOrder]) => ({
      id,
      sortOrder,
    }));
    if (items.length === 0) {
      exitReorderMode();
      return;
    }
    setSavingOrder(true);
    const ok = await manager.reorderCategories(items);
    setSavingOrder(false);
    if (ok) {
      pendingOrderRef.current.clear();
      setHasPendingOrder(false);
      setReorderMode(false);
      // Los sortOverrides quedan: el próximo reload los poda contra el cache
      // ya patcheado, sin flash del orden viejo.
    }
  }, [manager, exitReorderMode]);

  const renderTree = () => (
    <CategoryMasterTreeView
      groups={visibleGroups}
      canEdit={canEdit}
      togglingCategoryIds={manager.togglingCategoryIds}
      filterState={filterState}
      isWide={isWide}
      reordering={savingOrder}
      reorderMode={reorderMode}
      onOpenDetail={onOpenDetail}
      onToggleActive={onToggleActive}
      onMoveCategory={canEdit && reorderMode ? onMoveCategory : undefined}
    />
  );

  return (
    <View style={{ gap: spacing.lg }}>
      {reorderMode ? (
        <View style={styles.reorderBar}>
          <View style={styles.reorderBarText}>
            <Ionicons name="swap-vertical" size={16} color={ticketColors.ink2} />
            <Text style={styles.reorderHint}>
              Mové las categorías con las flechas. Las subcategorías se ordenan dentro de su
              categoría. Este orden se ve en tu tienda online y en los filtros del catálogo.
            </Text>
          </View>
          <View style={styles.reorderActions}>
            <Pressable
              onPress={onCancelOrder}
              disabled={savingOrder}
              style={({ pressed }) => [styles.reorderBtnGhost, pressed && { opacity: 0.7 }]}
              accessibilityRole="button"
              accessibilityLabel="Cancelar ordenamiento"
              testID="catalog-category-reorder-cancel"
            >
              <Text style={styles.reorderBtnGhostText}>Cancelar</Text>
            </Pressable>
            <Pressable
              onPress={() => void onSaveOrder()}
              disabled={!hasPendingOrder || savingOrder}
              style={({ pressed }) => [
                styles.reorderBtnPrimary,
                (!hasPendingOrder || savingOrder) && { opacity: 0.5 },
                pressed && hasPendingOrder && !savingOrder && { opacity: 0.9 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Guardar orden de categorías"
              testID="catalog-category-reorder-save"
            >
              <Text style={styles.reorderBtnPrimaryText}>
                {savingOrder ? 'Guardando…' : 'Guardar orden'}
              </Text>
            </Pressable>
          </View>
        </View>
      ) : (
        <MastersListFiltersPanel
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchLabel="Buscar categorías"
          searchPlaceholder="Buscar por nombre o código"
          stateOptions={FILTER_OPTIONS.map((opt) => ({ value: opt.id, label: opt.label }))}
          stateCounts={stats}
          filterState={filterState}
          onFilterStateChange={setFilterState}
          pageSize={10}
          onPageSizeChange={() => {}}
          layoutWide={isWide}
          trailingActions={
            <>
              <MastersSectionHeader
                canEdit={canEdit}
                editingMode={false}
                csvLabel="CSV"
                createLabel="Agregar"
                hideCreate
                onOpenCsv={onOpenCsv}
                onCreate={() => {}}
              />
              {canEdit ? (
                <>
                  <Pressable
                    onPress={enterReorderMode}
                    style={({ pressed }) => [styles.reorderCta, pressed && { opacity: 0.7 }]}
                    accessibilityRole="button"
                    accessibilityLabel="Ordenar categorías"
                    testID="catalog-category-reorder-cta"
                  >
                    <Ionicons name="swap-vertical-outline" size={14} color={ticketColors.ink2} />
                    <Text style={styles.reorderCtaText}>Ordenar</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => setCategoryRequestOpen(true)}
                    style={({ pressed }) => [styles.requestLinkBtn, pressed && { opacity: 0.7 }]}
                    accessibilityRole="button"
                    accessibilityLabel="Pedir categoría faltante"
                    testID="catalog-category-request-cta"
                  >
                    <Text style={styles.requestLink}>¿Falta alguna?</Text>
                  </Pressable>
                </>
              ) : null}
            </>
          }
        />
      )}

      <MastersSectionListShell
        loading={loading}
        error={error}
        itemCount={itemCount}
        totalCount={itemCount}
        pageIndex={0}
        pageSize={10}
        hasMore={false}
        listNouns={LIST_NOUNS}
        loadingLabel="Cargando categorías…"
        emptyIcon="layers-outline"
        emptyTitle="Sin categorías"
        emptyErrorTitle="No pudimos cargar categorías"
        emptyMessage="Las categorías vienen del rubro. Activá o desactivá las que uses."
        canEdit={false}
        createLabel="Agregar"
        onCreate={() => {}}
        onRetry={() => reload()}
        isWide={isWide}
        onPreviousPage={() => {}}
        onNextPage={() => {}}
        showPagination={false}
        renderWide={renderTree}
        renderMobile={renderTree}
      />

      <MasterDetailModal
        visible={!!detailCategory}
        entityType="category"
        entity={detailCategory!}
        canEdit={canEdit}
        onClose={onCloseDetail}
        onToggleActive={() => {
          if (detailCategory) {
            const nextActive = !detailCategory.isActive;
            onCloseDetail();
            onToggleActive(detailCategory, nextActive);
          }
        }}
      />

      <CatalogMasterConfirmModal
        spec={categoryConfirmSpec}
        onClose={() => setCategoryConfirmSpec(null)}
      />

      <CategoryRequestModal visible={categoryRequestOpen} onClose={() => setCategoryRequestOpen(false)} />
    </View>
  );
};

const styles = StyleSheet.create({
  requestLinkBtn: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  requestLink: {
    ...typography.caption,
    color: ticketColors.brandPrimary,
    fontWeight: '700',
  },
  reorderCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 44,
  },
  reorderCtaText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  reorderBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandBorderSoft,
    backgroundColor: ticketColors.brandSoft,
  },
  reorderBarText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
    minWidth: 220,
  },
  reorderHint: {
    ...typography.caption,
    color: ticketColors.brandInk,
    flex: 1,
    lineHeight: 18,
  },
  reorderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  reorderBtnGhost: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  reorderBtnGhostText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  reorderBtnPrimary: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    minHeight: 44,
    justifyContent: 'center',
  },
  reorderBtnPrimaryText: {
    ...typography.caption,
    color: ticketColors.goldInk,
    fontWeight: '800',
  },
});
